import {
  Combobox,
  ComboboxInput,
  ComboboxOptions,
  ComboboxOption,
} from '@headlessui/react'
import { useLazyQuery, useQuery } from '@apollo/client'
import { useEffect, useMemo, useRef, useState } from 'react'
import { searchPayers, getBridgePayer } from './queries.graphql'
import { Control, FieldErrors } from 'react-hook-form'
import useDebounce from 'hooks/use-debounce'
import useDynamicMaxHeight from 'hooks/use-dynamic-max-height'
import s from './styles.module.css'

interface PayerOption {
  value: string
  label: string
  memberId: string
}

interface PayerSearchProps {
  question: {
    id: number | string
    text: string
    isRequired?: boolean
    type: string
    component?: string
  }
  // RHF mode
  field?: {
    value: any
    onChange: (value: any) => void
    onBlur: () => void
  }
  control?: Control<any>
  errors?: FieldErrors<any>
  // Zustand mode
  value?: string
  error?: string
  onChange?: (value: string | null, metadata?: Record<string, any>) => void
  // Shared props
  clientId?: number
  responseByQuestionId?: Record<number, any>
  allQuestions?: Array<{ id: number | string }>
}

const PayerSearch: React.FC<PayerSearchProps> = ({
  field,
  value: zustandValue,
  onChange: zustandOnChange,
  // Accept but ignore other props for consistency
  question: _question,
  control: _control,
  errors: _errors,
  error: _error,
  clientId: _clientId,
  responseByQuestionId: _responseByQuestionId,
  allQuestions: _allQuestions,
}) => {
  const isRHFMode = !!field
  const payerId = isRHFMode ? field.value : zustandValue

  // Fetch payer details if payerId exists (payer ID string)
  const { data: existingPayerData } = useQuery(getBridgePayer, {
    variables: { bridgePayerId: payerId || '' },
    skip: !payerId,
  })

  // Convert payer data to PayerOption format
  const payerValue = useMemo(() => {
    if (!payerId) return null
    if (existingPayerData?.bridgePayer) {
      return {
        value: existingPayerData.bridgePayer.id,
        label: existingPayerData.bridgePayer.name,
        memberId: existingPayerData.bridgePayer.memberId || 'false',
      }
    }
    return null
  }, [payerId, existingPayerData])

  const [searchTerm, setSearchTerm] = useState('')
  const [isTyping, setIsTyping] = useState(false)
  const [search, { loading: payersLoading, data: searchData }] =
    useLazyQuery(searchPayers)

  // Calculate maxHeight as window height minus 200px, reactive to window resize
  const dropdownMaxHeight = useDynamicMaxHeight(200)

  const payerOptions = useMemo(() => {
    const searchOptions =
      searchData?.searchPayers?.map(payer => ({
        value: payer.id,
        label: payer.name,
        memberId: payer.memberId,
      })) || []
    return searchOptions
  }, [searchData])

  const debouncedSearch = useDebounce((value: string) => {
    if (value.length >= 2) {
      search({
        variables: { searchTerm: value || '' },
      })
    }
    setIsTyping(false)
  }, 300)

  const inputRef = useRef<HTMLInputElement>(null)
  const justSelectedRef = useRef<boolean>(false)

  useEffect(() => {
    if (payerValue) {
      setSearchTerm(payerValue.label)
    }
  }, [payerValue])

  const handleInputChange = (value: string) => {
    setIsTyping(true)
    setSearchTerm(value)
    // Clear selection when user starts typing different value
    if (payerValue && value !== payerValue.label) {
      if (isRHFMode) {
        field.onChange(null)
      } else {
        zustandOnChange?.(null)
      }
    }
    debouncedSearch(value)
  }

  const handleSelect = (option: PayerOption | null) => {
    // Ignore null calls that happen immediately after a valid selection
    // This prevents the double-call issue when Enter is pressed
    if (option === null && justSelectedRef.current) {
      justSelectedRef.current = false
      return
    }

    const payerIdValue = option ? option.value : null

    // Update search term to show the selected option's label immediately
    if (option) {
      setSearchTerm(option.label)
      // Mark that we just made a selection to prevent immediate null call
      justSelectedRef.current = true
      // Clear the flag after a short delay
      setTimeout(() => {
        justSelectedRef.current = false
      }, 100)
    }

    if (isRHFMode) {
      field.onChange(payerIdValue)
    } else {
      // For Zustand mode, pass both the payer ID and the full payer object as metadata
      const payerMetadata = option
        ? {
            name: option.label,
            memberIdRequired: option.memberId === 'true' ? true : false,
          }
        : undefined

      zustandOnChange?.(payerIdValue, payerMetadata)
    }
    // // Unfocus the input when an option is selected, only if the text input is not empty
    if (option !== null && option !== undefined) {
      inputRef.current?.blur()
    }
  }

  return (
    <div className={s.selectContainer}>
      <Combobox value={payerValue} onChange={handleSelect} nullable>
        {() => (
          <>
            <div className={s.comboboxWrapper}>
              <ComboboxInput
                ref={inputRef}
                className={s.comboboxInput}
                displayValue={() => searchTerm}
                onChange={event => handleInputChange(event.target.value)}
                placeholder="Search..."
                onFocus={() => {
                  inputRef.current?.scrollIntoView({
                    behavior: 'smooth',
                    block: 'center',
                  })
                }}
              />
            </div>
            {searchTerm && searchTerm.length >= 2 && !isTyping && (
              <ComboboxOptions
                className={s.comboboxOptions}
                style={{ maxHeight: dropdownMaxHeight }}>
                {searchTerm &&
                searchTerm.length >= 2 &&
                !isTyping &&
                searchTerm !== payerValue?.label ? (
                  payersLoading ? (
                    <div className={s.noResults}>Searching...</div>
                  ) : payerOptions.length > 0 ? (
                    payerOptions.map(option => (
                      <ComboboxOption
                        key={option.value}
                        value={option}
                        className={({ active, selected }) =>
                          `${s.comboboxOption} ${
                            active ? s.comboboxOptionActive : ''
                          } ${selected ? s.comboboxOptionSelected : ''}`
                        }>
                        {option.label}
                      </ComboboxOption>
                    ))
                  ) : (
                    <div className={s.noResults}>
                      No insurance companies found
                    </div>
                  )
                ) : searchTerm.length < 2 && searchTerm !== '' && !isTyping ? (
                  <div className={s.noResults}>
                    Type at least 2 characters to search
                  </div>
                ) : null}
              </ComboboxOptions>
            )}
          </>
        )}
      </Combobox>
    </div>
  )
}

export default PayerSearch
